import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
    LinearProgress,
    Chip,
} from "@mui/material";

/* Icons */
import CodeIcon from "@mui/icons-material/Code";
import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

/* Custom Components — import from your project */
// import RadioOption from "../../../components/RadioOption";
// import Typography from "../../../components/Typography";
// import LectureTimer from "../../../components/LectureTimer";
// import LanguageTabs from "../../../components/LanguageTabs";

/* ─── Background ─────────────────────────────────────────────────────────── */
const bgImage =
    "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&q=80";

/* Angular brand colours */
const NG_PRIMARY = "gold";
const NG_LIGHT   = "goldenrod";
const NG_DARK    = "#1A0A0E";

/* ─── Styled Components — exact mirror of Linux Dashboard ───────────────── */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(26,10,14,0.93), rgba(26,10,14,0.96)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(12),
        paddingBottom: theme.spacing(5),
    },
}));

const DashboardGlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(26,10,14,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
    border: "1px solid rgba(255,255,255,0.08)",
    height: "100%",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "all 0.25s ease",
    "&:hover": {
        backgroundColor: "rgba(221,0,49,0.08)",
        borderColor: NG_PRIMARY,
        transform: "translateX(3px)",
    },
}));

const ViewButton = styled(Button)(() => ({
    backgroundColor: NG_PRIMARY,
    color: "#fff",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    fontSize: "0.78rem",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": {
        backgroundColor: "#b5002a",
        transform: "scale(1.05)",
    },
}));

/* ─── Inline RadioOption ─────────────────────────────────────────────────── */
function RadioOption({ checked, onChange }) {
    return (
        <Box
            onClick={onChange}
            sx={{
                cursor: "pointer",
                color: checked ? NG_PRIMARY : "rgba(255,255,255,0.25)",
                flexShrink: 0, display: "flex", alignItems: "center",
            }}
        >
            {checked
                ? <CheckCircleOutlineIcon fontSize="small" />
                : <RadioButtonUncheckedIcon fontSize="small" />}
        </Box>
    );
}

/* ─── Inline LectureTimer ────────────────────────────────────────────────── */
function LectureTimer() {
    const [seconds, setSeconds] = useState(0);
    const [running, setRunning] = useState(false);

    useEffect(() => {
        if (!running) return;
        const id = setInterval(() => setSeconds(s => s + 1), 1000);
        return () => clearInterval(id);
    }, [running]);

    const fmt = s =>
        `${String(Math.floor(s / 3600)).padStart(2, "0")}:` +
        `${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:` +
        `${String(s % 60).padStart(2, "0")}`;

    return (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3, px: 2 }}>
            <Box sx={{
                display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
                justifyContent: "center",
                backgroundColor: "rgba(221,0,49,0.06)",
                border: "1px solid rgba(221,0,49,0.18)",
                borderRadius: "999px", px: 3, py: 1,
            }}>
                <Box sx={{ color: NG_PRIMARY, fontSize: "0.7rem", fontWeight: 700, letterSpacing: 1 }}>
                    STUDY TIMER
                </Box>
                <Box sx={{ fontFamily: "monospace", fontSize: "1.05rem", color: "white", minWidth: "82px", textAlign: "center" }}>
                    {fmt(seconds)}
                </Box>
                <Button size="small" onClick={() => setRunning(r => !r)} sx={{
                    backgroundColor: running ? "rgba(255,80,80,0.12)" : "rgba(221,0,49,0.12)",
                    color: running ? "#FF5050" : NG_PRIMARY,
                    borderRadius: "999px", px: 2, textTransform: "none",
                    fontWeight: 700, fontSize: "0.72rem",
                    "&:hover": { backgroundColor: running ? "rgba(255,80,80,0.22)" : "rgba(221,0,49,0.22)" },
                }}>
                    {running ? "Pause" : "Start"}
                </Button>
                <Button size="small" onClick={() => { setSeconds(0); setRunning(false); }} sx={{
                    color: "rgba(255,255,255,0.35)", borderRadius: "999px",
                    px: 1.5, textTransform: "none", fontSize: "0.72rem",
                    "&:hover": { color: "white" },
                }}>
                    Reset
                </Button>
            </Box>
        </Box>
    );
}

/* ─── Inline LanguageTabs ────────────────────────────────────────────────── */
const TABS = [
    { label: "React",        path: "/react-dashboard" },
    { label: "React Native", path: "/react-native-dashboard" },
    { label: "Angular",      path: "/angular-dashboard" },
];

function LanguageTabs({ active }) {
    return (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4, gap: 1.5, flexWrap: "wrap", px: 2 }}>
            {TABS.map(tab => (
                <Link key={tab.label} to={tab.path} style={{ textDecoration: "none" }}>
                    <Button sx={{
                        borderRadius: "999px", px: 3, py: 0.8,
                        fontWeight: 700, fontSize: "0.8rem", textTransform: "none",
                        backgroundColor: active === tab.label ? NG_PRIMARY : "rgba(255,255,255,0.05)",
                        color: active === tab.label ? "#fff" : "rgba(255,255,255,0.55)",
                        border: "1px solid",
                        borderColor: active === tab.label ? "transparent" : "rgba(255,255,255,0.08)",
                        "&:hover": {
                            backgroundColor: active === tab.label ? "#b5002a" : "rgba(221,0,49,0.08)",
                            color: active === tab.label ? "#fff" : NG_PRIMARY,
                            borderColor: "rgba(221,0,49,0.3)",
                        },
                    }}>
                        {tab.label}
                    </Button>
                </Link>
            ))}
        </Box>
    );
}

/* ─── Angular Topics — all chapters from the PDF book ───────────────────── */
const angularTopics = [
    // Chapter 1 — Introduction & TypeScript
    { title: "What is Angular? Angular vs React vs Vue",              link: "/ng/intro",               chapter: "Intro" },
    { title: "TypeScript Essentials for Angular",                     link: "/ng/typescript",          chapter: "Intro" },
    { title: "Setting Up Angular with the CLI",                       link: "/ng/setup-cli",           chapter: "Intro" },
    { title: "Angular Project Structure Explained",                   link: "/ng/project-structure",   chapter: "Intro" },
    { title: "Key Angular CLI Commands",                              link: "/ng/cli-commands",        chapter: "Intro" },
    // Chapter 2 — Components
    { title: "Anatomy of an Angular Component",                       link: "/ng/component-anatomy",   chapter: "Components" },
    { title: "Templates & Interpolation",                             link: "/ng/templates",           chapter: "Components" },
    { title: "@Input & @Output Decorators",                           link: "/ng/input-output",        chapter: "Components" },
    { title: "Component Lifecycle Hooks",                             link: "/ng/lifecycle",           chapter: "Components" },
    { title: "ViewChild & ContentChild",                              link: "/ng/viewchild",           chapter: "Components" },
    { title: "Standalone Components (Angular 17+)",                   link: "/ng/standalone",          chapter: "Components" },
    // Chapter 3 — Data Binding & Directives
    { title: "Interpolation, Property & Event Binding",               link: "/ng/data-binding",        chapter: "Binding & Directives" },
    { title: "Two-Way Binding with ngModel",                          link: "/ng/ngmodel",             chapter: "Binding & Directives" },
    { title: "Built-in Structural Directives (ngIf, ngFor, ngSwitch)", link: "/ng/structural-dir",     chapter: "Binding & Directives" },
    { title: "New Control Flow — @if, @for, @switch",                 link: "/ng/new-control-flow",    chapter: "Binding & Directives" },
    { title: "Built-in Attribute Directives (ngClass, ngStyle)",      link: "/ng/attribute-dir",       chapter: "Binding & Directives" },
    { title: "Custom Attribute Directives",                           link: "/ng/custom-directives",   chapter: "Binding & Directives" },
    { title: "Pipes — Built-in (date, currency, async)",              link: "/ng/pipes-builtin",       chapter: "Binding & Directives" },
    { title: "Custom Pipes",                                          link: "/ng/custom-pipes",        chapter: "Binding & Directives" },
    // Chapter 4 — Services & DI
    { title: "What Are Services & Why Use Them?",                     link: "/ng/services-intro",      chapter: "Services & DI" },
    { title: "Creating & Providing Services",                         link: "/ng/creating-services",   chapter: "Services & DI" },
    { title: "Dependency Injection Hierarchy",                        link: "/ng/di-hierarchy",        chapter: "Services & DI" },
    { title: "HttpClient — GET, POST, PUT, DELETE",                   link: "/ng/http-client",         chapter: "Services & DI" },
    { title: "HTTP Interceptors (Auth & Loading)",                    link: "/ng/interceptors",        chapter: "Services & DI" },
    // Chapter 5 — Angular Router
    { title: "Setting Up Angular Router",                             link: "/ng/router-setup",        chapter: "Routing" },
    { title: "Route Parameters & Query Params",                       link: "/ng/route-params",        chapter: "Routing" },
    { title: "Nested & Child Routes",                                 link: "/ng/nested-routes",       chapter: "Routing" },
    { title: "Lazy Loading Feature Modules",                          link: "/ng/lazy-loading",        chapter: "Routing" },
    { title: "Route Guards — canActivate & canDeactivate",            link: "/ng/guards",              chapter: "Routing" },
    { title: "Resolvers — Pre-loading Route Data",                    link: "/ng/resolvers",           chapter: "Routing" },
    // Chapter 6 — Angular Forms
    { title: "Template-Driven Forms",                                 link: "/ng/template-forms",      chapter: "Forms" },
    { title: "Reactive Forms with FormBuilder",                       link: "/ng/reactive-forms",      chapter: "Forms" },
    { title: "FormGroup, FormControl & FormArray",                    link: "/ng/formgroup",           chapter: "Forms" },
    { title: "Built-in Validators",                                   link: "/ng/validators",          chapter: "Forms" },
    { title: "Custom Validators & Cross-Field Validation",            link: "/ng/custom-validators",   chapter: "Forms" },
    { title: "Async Validators",                                      link: "/ng/async-validators",    chapter: "Forms" },
    // Chapter 7 — RxJS
    { title: "Observables, Observers & Subscriptions",                link: "/ng/rxjs-basics",         chapter: "RxJS" },
    { title: "Core RxJS Operators (map, filter, switchMap…)",         link: "/ng/rxjs-operators",      chapter: "RxJS" },
    { title: "Subject, BehaviorSubject & ReplaySubject",              link: "/ng/rxjs-subjects",       chapter: "RxJS" },
    { title: "Combining Observables (combineLatest, forkJoin)",       link: "/ng/rxjs-combine",        chapter: "RxJS" },
    { title: "Error Handling in RxJS",                                link: "/ng/rxjs-errors",         chapter: "RxJS" },
    { title: "Async Pipe — Auto Subscribe & Unsubscribe",             link: "/ng/async-pipe",          chapter: "RxJS" },
    // Chapter 8 — Angular 17 Signals
    { title: "What Are Signals? — Angular 17",                        link: "/ng/signals-intro",       chapter: "Signals" },
    { title: "signal(), computed() & effect()",                       link: "/ng/signal-primitives",   chapter: "Signals" },
    { title: "Signals vs Observables — When to Use Which",            link: "/ng/signals-vs-rxjs",     chapter: "Signals" },
    { title: "Signals in Services for State Management",              link: "/ng/signals-state",       chapter: "Signals" },
    { title: "Deferrable Views (@defer)",                             link: "/ng/defer",               chapter: "Signals" },
    // Chapter 9 — NgRx State Management
    { title: "Redux Pattern — Actions, Reducers, Store",              link: "/ng/ngrx-overview",       chapter: "NgRx" },
    { title: "NgRx Store Setup",                                      link: "/ng/ngrx-setup",          chapter: "NgRx" },
    { title: "NgRx Actions & Reducers",                               link: "/ng/ngrx-actions",        chapter: "NgRx" },
    { title: "NgRx Selectors — Querying State",                       link: "/ng/ngrx-selectors",      chapter: "NgRx" },
    { title: "NgRx Effects — Side Effects & HTTP",                    link: "/ng/ngrx-effects",        chapter: "NgRx" },
    { title: "NgRx Entity — Normalised Collections",                  link: "/ng/ngrx-entity",         chapter: "NgRx" },
    // Chapter 10 — Performance
    { title: "OnPush Change Detection Strategy",                      link: "/ng/onpush",              chapter: "Performance" },
    { title: "trackBy in ngFor",                                      link: "/ng/trackby",             chapter: "Performance" },
    { title: "Lazy Loading & Code Splitting",                         link: "/ng/perf-lazy",           chapter: "Performance" },
    { title: "Pure Pipes for Memoization",                            link: "/ng/pure-pipes",          chapter: "Performance" },
    { title: "Angular DevTools Profiling",                            link: "/ng/devtools",            chapter: "Performance" },
    // Chapter 11 — Testing
    { title: "TestBed Setup & Component Testing",                     link: "/ng/testbed",             chapter: "Testing" },
    { title: "Testing Services & HTTP (HttpTestingController)",       link: "/ng/testing-services",    chapter: "Testing" },
    { title: "Testing Pipes, Directives & Guards",                    link: "/ng/testing-misc",        chapter: "Testing" },
    { title: "E2E Testing with Cypress",                              link: "/ng/cypress",             chapter: "Testing" },
    // Chapter 12 — Projects
    { title: "Project: Employee Management System",                   link: "/ng/project-employees",   chapter: "Projects" },
    { title: "Project: Blog Platform with NgRx",                      link: "/ng/project-blog",        chapter: "Projects" },
    { title: "Project: Real-Time Dashboard with RxJS",                link: "/ng/project-dashboard",   chapter: "Projects" },
    { title: "Project: Products Dashboard (HTTP + Interceptors)",     link: "/ng/project-products",    chapter: "Projects" },
];

const chapterColors = {
    "Intro":                "#FF6E6E",
    "Components":           "#A78BFA",
    "Binding & Directives": "#34D399",
    "Services & DI":        "#61DAFB",
    "Routing":              "#F59E0B",
    "Forms":                "#FB923C",
    "RxJS":                 "#60A5FA",
    "Signals":              "#E879F9",
    "NgRx":                 "#DD0031",
    "Performance":          "#4ADE80",
    "Testing":              "#FCD34D",
    "Projects":             "#FBBF24",
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
export default function AngularDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* State Management */
    const [searchQuery,   setSearchQuery]   = useState("");
    const [showScroll,    setShowScroll]    = useState(false);
    const [completed,     setCompleted]     = useState({});
    const [activeChapter, setActiveChapter] = useState("All");

    const completedCount  = Object.values(completed).filter(Boolean).length;
    const progressPercent = (completedCount / angularTopics.length) * 100;
    const allChapters     = ["All", ...Object.keys(chapterColors)];

    /* Logic: Search Filtering & Progress */
    const filteredTopics = useMemo(() => {
        return angularTopics.filter(t => {
            const matchSearch  = t.title.toLowerCase().includes(searchQuery.toLowerCase());
            const matchChapter = activeChapter === "All" || t.chapter === activeChapter;
            return matchSearch && matchChapter;
        });
    }, [searchQuery, activeChapter]);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
    const toggleComplete  = title => setCompleted(prev => ({ ...prev, [title]: !prev[title] }));

    /* ── RENDER ──────────────────────────────────────────────────────────── */
    return (
        <PageWrapper>

            {/* ── BANNER ── */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 16, px: 2 }}>
                <Box
                    sx={{
                        fontWeight: 800,
                        color: NG_PRIMARY,
                        fontSize: isMobile ? "1.4rem" : "2.7rem",
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 30px rgba(221,0,49,0.25)",
                        letterSpacing: "0.04em",
                    }}
                >
                    A  MASTERING ANGULAR
                </Box>
                <Box sx={{ mt: 1.5, color: "rgba(255,255,255,0.5)", fontSize: "0.92rem" }}>
                    Enterprise Web Development · TypeScript · RxJS · NgRx · Angular 17 Signals
                </Box>
                <Box sx={{ mt: 0.8, color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
                    By{" "}
                    <Box component="span" sx={{ color: NG_PRIMARY, fontWeight: 700 }}>
                        Ajao Hazzan
                    </Box>{" "}
                    · Full-Stack Software Engineer
                </Box>
            </Box>

            {/* ── TIMER & TABS ── */}
            <LectureTimer />
            <LanguageTabs active="Angular" />

            {/* ── MAIN CONTENT ── */}
            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>

                {/* ── LEFT COLUMN: Overview & Image ── */}
                <Grid item xs={12} md={5}>
                    <DashboardGlassCard>
                        <Box sx={{
                            fontWeight: 700, textAlign: "center", mb: 2,
                            color: NG_PRIMARY, fontSize: "0.9rem", letterSpacing: "0.08em",
                        }}>
                            ANGULAR FUNDAMENTALS
                        </Box>

                        <Card sx={{
                            width: "100%",
                            background: "linear-gradient(135deg, #0D0305, #1A0208)",
                            textAlign: "center",
                            padding: "15px",
                            borderRadius: "12px",
                            mt: 2,
                            border: "1px solid rgba(221,0,49,0.18)",
                        }}>
                            <CardMedia
                                component="img"
                                height={isMobile ? "180" : "220"}
                                image="https://upload.wikimedia.org/wikipedia/commons/thumb/c/cf/Angular_full_color_logo.svg/1200px-Angular_full_color_logo.svg.png"
                                alt="Angular Logo"
                                sx={{ objectFit: "contain", p: 2 }}
                            />

                            <CardContent sx={{ color: "white" }}>
                                <Box sx={{ fontWeight: 800, fontSize: "1rem", mb: 0.5, color: NG_PRIMARY }}>
                                    Enterprise Web Development
                                </Box>
                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                                    <PersonalVideoIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.5)" }} />
                                    <Box sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.65)", fontWeight: 600 }}>
                                        TypeScript · DI · RxJS · NgRx
                                    </Box>
                                </Stack>

                                <Link to="/angular-playground" style={{ textDecoration: "none" }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<CodeIcon />}
                                        sx={{
                                            background: `linear-gradient(135deg, ${NG_PRIMARY}, #b5002a)`,
                                            color: "#fff",
                                            fontWeight: "bold",
                                            py: 1.5,
                                            borderRadius: "8px",
                                            textTransform: "none",
                                            fontSize: "0.9rem",
                                            "&:hover": { background: "linear-gradient(135deg, #b5002a, #DD0031)" },
                                        }}
                                    >
                                        Open Angular Playground
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>

                        {/* Stats grid */}
                        <Grid container spacing={1.5} sx={{ mt: 2 }}>
                            {[
                                { label: "Total Topics", value: angularTopics.length,    color: NG_PRIMARY },
                                { label: "Completed",    value: completedCount,           color: "#34D399" },
                                { label: "Chapters",     value: Object.keys(chapterColors).length, color: "#A78BFA" },
                                { label: "Progress",     value: `${Math.round(progressPercent)}%`, color: "#F59E0B" },
                            ].map(s => (
                                <Grid item xs={6} key={s.label}>
                                    <Box sx={{
                                        backgroundColor: "rgba(255,255,255,0.04)",
                                        border: "1px solid rgba(255,255,255,0.07)",
                                        borderRadius: "10px", p: 1.5, textAlign: "center",
                                    }}>
                                        <Box sx={{ fontSize: "1.5rem", fontWeight: 800, color: s.color }}>{s.value}</Box>
                                        <Box sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{s.label}</Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Chapter legend */}
                        <Box sx={{ mt: 3 }}>
                            <Box sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)", mb: 1.5, fontWeight: 700, letterSpacing: "0.1em" }}>
                                CHAPTER GUIDE
                            </Box>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {Object.entries(chapterColors).map(([ch, col]) => (
                                    <Box
                                        key={ch}
                                        onClick={() => setActiveChapter(activeChapter === ch ? "All" : ch)}
                                        sx={{
                                            display: "flex", alignItems: "center", gap: 0.6,
                                            cursor: "pointer",
                                            opacity: activeChapter === "All" || activeChapter === ch ? 1 : 0.4,
                                            transition: "opacity 0.2s",
                                        }}
                                    >
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: col, flexShrink: 0 }} />
                                        <Box sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.55)" }}>{ch}</Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </DashboardGlassCard>
                </Grid>

                {/* ── RIGHT COLUMN: Curriculum with Progress & Search ── */}
                <Grid item xs={12} md={7}>
                    <DashboardGlassCard>
                        <Box sx={{ fontWeight: 700, textAlign: "center", mb: 3, letterSpacing: "0.06em", fontSize: "0.9rem" }}>
                            CURRICULUM MODULES
                        </Box>

                        {/* Progress Tracker Section */}
                        <Box sx={{ mb: 3, px: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Box sx={{ color: NG_PRIMARY, fontWeight: "bold", fontSize: "0.75rem" }}>
                                    Angular Mastery
                                </Box>
                                <Box sx={{ color: NG_PRIMARY, fontWeight: "bold", fontSize: "0.75rem" }}>
                                    {Math.round(progressPercent)}%
                                </Box>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercent}
                                sx={{
                                    height: 8, borderRadius: 5,
                                    bgcolor: "rgba(255,255,255,0.08)",
                                    "& .MuiLinearProgress-bar": {
                                        background: `linear-gradient(90deg, ${NG_PRIMARY}, #b5002a)`,
                                        borderRadius: 5,
                                    },
                                }}
                            />
                            <Box sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.3)", mt: 0.8, textAlign: "right" }}>
                                {completedCount} of {angularTopics.length} topics completed
                            </Box>
                        </Box>

                        {/* Chapter filter chips */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 2.5 }}>
                            {allChapters.map(ch => (
                                <Chip
                                    key={ch}
                                    label={ch}
                                    size="small"
                                    onClick={() => setActiveChapter(ch)}
                                    sx={{
                                        cursor: "pointer",
                                        backgroundColor: activeChapter === ch
                                            ? (chapterColors[ch] || NG_PRIMARY)
                                            : "rgba(255,255,255,0.05)",
                                        color: activeChapter === ch ? "#0A0C1E" : "rgba(255,255,255,0.55)",
                                        fontWeight: activeChapter === ch ? 700 : 500,
                                        fontSize: "0.68rem",
                                        border: "1px solid",
                                        borderColor: activeChapter === ch ? "transparent" : "rgba(255,255,255,0.07)",
                                        "&:hover": {
                                            backgroundColor: chapterColors[ch]
                                                ? `${chapterColors[ch]}28`
                                                : "rgba(221,0,49,0.1)",
                                        },
                                    }}
                                />
                            ))}
                        </Box>

                        {/* Search Component */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search Angular topics..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: NG_PRIMARY, fontSize: "1.1rem" }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.04)",
                                    borderRadius: "8px",
                                    fontSize: "0.88rem",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(221,0,49,0.2)" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(221,0,49,0.4)" },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: NG_PRIMARY },
                                },
                            }}
                            inputProps={{ style: { color: "white" } }}
                        />

                        {/* Topic list */}
                        <Box sx={{
                            maxHeight: "520px", overflowY: "auto", pr: 0.5,
                            "&::-webkit-scrollbar": { width: "4px" },
                            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                            "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(221,0,49,0.25)", borderRadius: "4px" },
                        }}>
                            <Stack spacing={1.5}>
                                {filteredTopics.length > 0 ? (
                                    filteredTopics.map((topic, i) => (
                                        <TopicBox key={i}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1, minWidth: 0 }}>
                                                <RadioOption
                                                    checked={!!completed[topic.title]}
                                                    onChange={() => toggleComplete(topic.title)}
                                                />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Box sx={{
                                                        fontSize: "0.85rem", fontWeight: 500,
                                                        color: completed[topic.title] ? "rgba(255,255,255,0.35)" : "white",
                                                        textDecoration: completed[topic.title] ? "line-through" : "none",
                                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                    }}>
                                                        {topic.title}
                                                    </Box>
                                                    <Box sx={{
                                                        fontSize: "0.62rem", fontWeight: 600, mt: 0.2,
                                                        color: chapterColors[topic.chapter] || NG_PRIMARY,
                                                    }}>
                                                        {topic.chapter}
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Link to={topic.link} style={{ textDecoration: "none" }}>
                                                <ViewButton size="small">View</ViewButton>
                                            </Link>
                                        </TopicBox>
                                    ))
                                ) : (
                                    <Box sx={{ textAlign: "center", opacity: 0.4, py: 4, fontSize: "0.9rem" }}>
                                        No modules found for "{searchQuery}"
                                    </Box>
                                )}
                            </Stack>
                        </Box>

                        {/* Footer row */}
                        <Box sx={{
                            mt: 2, pt: 2,
                            borderTop: "1px solid rgba(255,255,255,0.06)",
                            display: "flex", justifyContent: "space-between",
                            alignItems: "center", flexWrap: "wrap", gap: 1,
                        }}>
                            <Box sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.3)" }}>
                                {filteredTopics.length} of {angularTopics.length} topics shown
                            </Box>
                            {completedCount > 0 && (
                                <Box
                                    onClick={() => setCompleted({})}
                                    sx={{ fontSize: "0.7rem", color: "rgba(255,80,80,0.5)", cursor: "pointer", "&:hover": { color: "#FF5050" } }}
                                >
                                    Reset progress
                                </Box>
                            )}
                        </Box>
                    </DashboardGlassCard>
                </Grid>
            </Grid>

            {/* Back to Top Floating Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{
                        position: "fixed", bottom: 25, right: 25,
                        background: `linear-gradient(135deg, ${NG_PRIMARY}, #b5002a)`,
                        "&:hover": { background: "linear-gradient(135deg, #b5002a, #DD0031)" },
                    }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#fff" }} />
                </Fab>
            </Zoom>

        </PageWrapper>
    );
}
